import React, { useState, useEffect, useMemo, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { getStoredUser, apiGet, apiPost, apiDelete } from '../lib/api';
import { sendWorkerAiFullRequest } from '../lib/workerAi';
import { DailyRecord } from '../types';
import { loadAppState, saveAppState, toPersianDigits } from '../lib/storage';
import { useLanguage, formatNumberByLang } from '../lib/i18n';
import { formatGregorianDate } from '../lib/dateUtils';
import VoiceInputButton from './VoiceInputButton';

// Custom Markdown components for doctor response rendering
const doctorMarkdownComponents = {
  h1: ({ children }: any) => (
    <h1 className="text-base sm:text-lg font-black text-teal-900 dark:text-teal-200 mt-5 mb-2 pb-1 border-b-2 border-teal-500/40 flex items-center gap-2">
      <Stethoscope className="w-4 h-4 text-teal-600 shrink-0" />
      <span>{children}</span>
    </h1>
  ),
  h2: ({ children }: any) => (
    <h2 className="text-sm sm:text-base font-extrabold text-teal-800 dark:text-teal-300 mt-4 mb-2 flex items-center gap-2">
      <span className="w-2 h-2 rounded-full bg-teal-500 inline-block"></span>
      <span>{children}</span>
    </h2>
  ),
  h3: ({ children }: any) => (
    <h3 className="text-xs sm:text-sm font-bold text-teal-700 dark:text-teal-400 mt-3 mb-1.5 flex items-center gap-1.5">
      <span>{children}</span>
    </h3>
  ),
  strong: ({ children }: any) => (
    <strong className="font-extrabold text-teal-950 dark:text-teal-100 bg-teal-100/60 dark:bg-teal-900/50 px-1.5 py-0.5 rounded text-teal-950 dark:text-teal-100">
      {children}
    </strong>
  ),
  table: ({ children }: any) => (
    <div className="overflow-x-auto my-4 rounded-2xl border border-teal-200/80 dark:border-teal-800/80 shadow-xs">
      <table className="w-full text-right text-xs border-collapse bg-white dark:bg-gray-900">
        {children}
      </table>
    </div>
  ),
  thead: ({ children }: any) => (
    <thead className="bg-teal-100/90 dark:bg-teal-900/80 text-teal-950 dark:text-teal-100 font-black">
      {children}
    </thead>
  ),
  th: ({ children }: any) => (
    <th className="p-2.5 sm:p-3 text-right font-black border-b-2 border-teal-200 dark:border-teal-800 text-teal-950 dark:text-teal-100">
      {children}
    </th>
  ),
  td: ({ children }: any) => (
    <td className="p-2.5 sm:p-3 border-b border-teal-100/60 dark:border-teal-900/40 text-gray-800 dark:text-gray-200 align-top leading-relaxed">
      {children}
    </td>
  ),
  tr: ({ children }: any) => (
    <tr className="even:bg-teal-50/40 dark:even:bg-teal-950/20 hover:bg-teal-100/30 dark:hover:bg-teal-900/30 transition-colors">
      {children}
    </tr>
  ),
  ul: ({ children }: any) => (
    <ul className="my-2.5 space-y-1.5 list-disc list-inside text-gray-800 dark:text-gray-200 leading-relaxed pr-2">
      {children}
    </ul>
  ),
  ol: ({ children }: any) => (
    <ol className="my-2.5 space-y-1.5 list-decimal list-inside text-gray-800 dark:text-gray-200 leading-relaxed pr-2">
      {children}
    </ol>
  ),
  li: ({ children }: any) => (
    <li className="text-xs sm:text-sm text-gray-800 dark:text-gray-200 leading-relaxed">
      {children}
    </li>
  ),
  p: ({ children }: any) => (
    <p className="my-2 leading-loose text-xs sm:text-sm text-gray-800 dark:text-gray-200">
      {children}
    </p>
  ),
  blockquote: ({ children }: any) => (
    <blockquote className="my-3 p-3.5 border-r-4 border-amber-500 bg-amber-50 dark:bg-amber-950/40 text-amber-900 dark:text-amber-200 rounded-l-2xl text-xs sm:text-sm leading-relaxed shadow-xs">
      {children}
    </blockquote>
  ),
};

// Icons
import { 
  HeartPulse, Stethoscope, Sparkles, User, Activity, AlertTriangle, AlertCircle,
  Pencil, Save, X, Send, RefreshCw, Copy, Check, Trash2, Calendar, 
  FileText, ShieldAlert, Pill, Flame, Droplets, ArrowRight, MessageSquare, Cpu
} from 'lucide-react';

export interface MedicalProfile {
  age?: number | string;
  gender?: 'male' | 'female' | 'other';
  height?: number | string;
  weight?: number | string;
  activityLevel?: 'sedentary' | 'light' | 'moderate' | 'high' | 'athlete';
  medicalConditions?: string;
  medications?: string;
  allergies?: string;
  dietaryGoals?: string;
  updatedAt?: string;
}

export interface DoctorConsultation {
  id: string;
  question: string;
  answer: string;
  createdAt: string;
  userProfileSnapshot?: MedicalProfile;
  dailyContextSnapshot?: {
    caloriesLoggedToday?: number;
    waterMlToday?: number;
    weightToday?: number;
  };
  aiModel?: string;
  aiProvider?: string;
  responseTimeMs?: number;
}

const getActivityLevelLabel = (level: string, t: any): string => {
  const map: Record<string, string> = {
    sedentary: t('activitySedentary', 'کم‌تحرک (کار پشت‌میزی / بدون ورزش)'),
    light: t('activityLight', 'فعالیت سبک (۱-۲ روز ورزش در هفته)'),
    moderate: t('activityModerate', 'فعالیت متوسط (۳-۵ روز ورزش در هفته)'),
    high: t('activityHigh', 'پرتلاش و فعال (۶-۷ روز ورزش سنگین)'),
    athlete: t('activityAthlete', 'ورزشکار حرفه‌ای / کار فیزیکی سنگین')
  };
  return map[level] || map['moderate'];
};

const getLocalizedMedicalText = (text: string | undefined | null, type: 'cond' | 'med' | 'allergy' | 'goal', t: any): string => {
  if (!text || !text.trim()) {
    if (type === 'cond') return t('medCondNone', 'ندارم');
    if (type === 'med') return t('medsNone', 'مصرف نمی‌کنم');
    if (type === 'allergy') return t('allergiesNone', 'بدون حساسیت خاص');
    return t('goalsDefault', 'کاهش چربی شکمی و بهبود سلامت');
  }

  const trimmed = text.trim();
  const lower = trimmed.toLowerCase();
  if (trimmed === 'ندارم' || trimmed === 'هیچ' || lower === 'none' || lower === 'inga') {
    return t('medCondNone', 'ندارم');
  }
  if (trimmed === 'مصرف نمی‌کنم' || trimmed === 'مصرف نمیکنم' || lower === 'no medications' || lower === 'inga mediciner') {
    return t('medsNone', 'مصرف نمی‌کنم');
  }
  if (trimmed === 'بدون حساسیت' || trimmed === 'بدون حساسیت خاص' || trimmed === 'هیچ حساسیت شناخته‌شده‌ای ندارد' || lower === 'no allergies' || lower === 'inga allergier') {
    return t('allergiesNone', 'بدون حساسیت خاص');
  }

  return text;
};

interface Props {
  record?: DailyRecord;
  onNavigateToTab?: (tab: string) => void;
}

export default function PersonalDoctorModule({ record }: Props) {
  const { lang, t } = useLanguage();
  const currentUser = getStoredUser();
  const userId = currentUser ? currentUser.id : 'guest_user';

  // State: Medical Profile
  const [profile, setProfile] = useState<MedicalProfile>({
    age: '35',
    gender: 'male',
    height: '175',
    weight: '78',
    activityLevel: 'moderate',
    medicalConditions: t('medCondDefault', 'کبد چرب درجه ۱، گهگاه فشار خون'),
    medications: t('medsDefault', 'مکمل ویتامین D3، امگا ۳'),
    allergies: t('allergiesDefault', 'هیچ حساسیت شناخته‌شده‌ای ندارد'),
    dietaryGoals: t('goalsDefault', 'کاهش چربی شکمی، تنظیم قند خون و افزایش سلامت متابولیک')
  });

  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState<MedicalProfile>({});
  const [isSavingProfile, setIsSavingProfile] = useState(false);

  // State: Consultations History
  const [consultations, setConsultations] = useState<DoctorConsultation[]>([]);
  const [isLoadingConsultations, setIsLoadingConsultations] = useState(true);
  const [consultationError, setConsultationError] = useState<string | null>(null);

  // State: New Query
  const [userQuery, setUserQuery] = useState('');
  const [isAskingAi, setIsAskingAi] = useState(false);
  const [currentAnswer, setCurrentAnswer] = useState<string | null>(null);
  const [currentModelInfo, setCurrentModelInfo] = useState<{ model?: string; provider?: string; responseTimeMs?: number } | null>(null);
  const [copiedAnswer, setCopiedAnswer] = useState(false);

  // Helper to parse Persian/Arabic numbers safely into standard integers/floats
  const parsePersianInt = (val: any, fallback: number): number => {
    if (val === undefined || val === null || val === '') return fallback;
    if (typeof val === 'number') return isNaN(val) ? fallback : val;
    const str = String(val).replace(/[۰-۹]/g, d => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d).toString()).trim();
    const parsed = parseInt(str, 10);
    return isNaN(parsed) ? fallback : parsed;
  };

  const parsePersianFloat = (val: any, fallback: number): number => {
    if (val === undefined || val === null || val === '') return fallback;
    if (typeof val === 'number') return isNaN(val) ? fallback : val;
    const str = String(val).replace(/[۰-۹]/g, d => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d).toString()).trim();
    const parsed = parseFloat(str);
    return isNaN(parsed) ? fallback : parsed;
  };

  // 1. Load profile from local storage / AppState
  useEffect(() => {
    const state = loadAppState();
    const s = state.settings;
    setProfile({
      age: s.age ?? 35,
      gender: s.gender ?? 'male',
      height: s.height ?? 175,
      weight: s.currentWeight ?? 78,
      activityLevel: (s.activityLevel as any) ?? 'moderate',
      medicalConditions: s.medicalConditions ?? t('medCondDefault', 'کبد چرب درجه ۱، گهگاه فشار خون'),
      medications: s.medications ?? t('medsDefault', 'مکمل ویتامین D3، امگا ۳'),
      allergies: s.allergies ?? t('allergiesNone', 'بدون حساسیت خاص'),
      dietaryGoals: s.dietaryGoals ?? t('goalsWeightLoss', 'کاهش وزن سالم و بهبود شاخص‌های کبد'),
      updatedAt: new Date().toISOString()
    });
  }, [userId]);

  // 2. Load consultations directly from local server REST API
  const fetchConsultations = useCallback(() => {
    if (!userId) return;
    setIsLoadingConsultations(true);
    setConsultationError(null);

    apiGet<any[]>('/api/doctor/consultations')
      .then(res => {
        if (!res.success || !Array.isArray(res.data)) {
          throw new Error(res.error || 'Failed to fetch consultations');
        }
        const data = res.data;
        const loaded: DoctorConsultation[] = data.map((item: any) => ({
          id: item.id,
          question: item.question || '',
          answer: item.answer || '',
          createdAt: item.createdAt || item.created_at || item.timestamp || new Date().toISOString(),
          userProfileSnapshot: item.userProfileSnapshot || (item.user_profile_snapshot ? (typeof item.user_profile_snapshot === 'string' ? JSON.parse(item.user_profile_snapshot) : item.user_profile_snapshot) : {}),
          dailyContextSnapshot: item.dailyContextSnapshot || (item.daily_context_snapshot ? (typeof item.daily_context_snapshot === 'string' ? JSON.parse(item.daily_context_snapshot) : item.daily_context_snapshot) : {}),
          aiModel: item.model_name || item.model || item.aiModel || '',
          aiProvider: item.aiProvider || item.ai_provider || 'google',
          responseTimeMs: item.response_time_ms || item.responseTimeMs
        }));

        loaded.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        setConsultations(loaded);
      })
      .catch(err => {
        console.error('REST AI consultations fetch error:', err);
        setConsultationError(err?.message || 'خطا در دریافت مشاوره‌های ابری از پایگاه داده');
      })
      .finally(() => setIsLoadingConsultations(false));
  }, [userId]);

  useEffect(() => {
    fetchConsultations();
  }, [fetchConsultations]);

  // Handle Save Profile (Saves to single unified AppState settings)
  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSavingProfile(true);
    try {
      const state = loadAppState();
      const ageVal = parsePersianInt(profileForm.age, state.settings.age || 35);
      const heightVal = parsePersianInt(profileForm.height, state.settings.height || 175);
      const weightVal = parsePersianFloat(profileForm.weight, state.settings.currentWeight || 78);

      const updatedSettings = {
        ...state.settings,
        age: ageVal,
        gender: profileForm.gender || state.settings.gender || 'male',
        height: heightVal,
        currentWeight: weightVal,
        activityLevel: (profileForm.activityLevel as any) || state.settings.activityLevel || 'moderate',
        medicalConditions: profileForm.medicalConditions ?? state.settings.medicalConditions,
        medications: profileForm.medications ?? state.settings.medications,
        allergies: profileForm.allergies ?? state.settings.allergies,
        dietaryGoals: profileForm.dietaryGoals ?? state.settings.dietaryGoals,
      };

      const newState = { ...state, settings: updatedSettings };
      saveAppState(newState, true);

      setProfile({
        age: updatedSettings.age,
        gender: updatedSettings.gender,
        height: updatedSettings.height,
        weight: updatedSettings.currentWeight,
        activityLevel: updatedSettings.activityLevel as any,
        medicalConditions: updatedSettings.medicalConditions,
        medications: updatedSettings.medications,
        allergies: updatedSettings.allergies,
        dietaryGoals: updatedSettings.dietaryGoals,
        updatedAt: new Date().toISOString()
      });

      setIsEditingProfile(false);
    } catch (err: any) {
      alert(t('doctorProfileSaveError', 'خطا در ذخیره‌سازی پرونده سلامت: ') + (err?.message || err));
    } finally {
      setIsSavingProfile(false);
    }
  };

  // Open Edit Profile Modal
  const openEditProfile = () => {
    setProfileForm({ ...profile });
    setIsEditingProfile(true);
  };

  // Compute Daily Context from record
  const dailyCalories = useMemo(() => {
    if (!record || !record.foods) return 0;
    return record.foods.reduce((acc, f) => acc + (f.calories || 0), 0);
  }, [record]);

  const dailyWater = useMemo(() => {
    if (!record || !record.waterIntake) return 0;
    // If waterIntake is less than 50, assume it is glasses and multiply by 250ml
    return record.waterIntake < 50 ? record.waterIntake * 250 : record.waterIntake;
  }, [record]);

  // Handle Ask AI Doctor
  const handleAskDoctor = async (customPrompt?: string) => {
    const questionToAsk = customPrompt || userQuery;
    if (!questionToAsk.trim() || isAskingAi) return;

    setIsAskingAi(true);
    setCurrentAnswer(null);

    // Build rich, clinical prompt incorporating exact user profile and live records
    const systemPrompt = `شما یک پزشک فوق‌تخصص برجسته، دلسوز و با‌تجربه تغذیه بالینی، متابولیسم و کبد چرب هستید.
مهم و حیاتی: هرگز و تحت هیچ شرایطی مقدمه‌چینی، تعریف کلی از سیستم‌های هوش مصنوعی یا توضیحات عمومی ننویسید! بلافاصله و با لحن یک پزشک واقعی، پاسخ تخصصی و مستقیم به سوال بیمار بدهید.

قوانین زبان و فرمت (الزامی):
۱. تمام متن پاسخ باید ۱۰۰٪ به زبان فارسی روان، محترمانه و شیوا نوشته شود. استفاده از حروف یا کلمات کره‌ای، سیریلیک، انگلیسی یا متون خرابی اکیداً ممنوع است.
۲. کلمات پزشکی مانند متابولیسم، کبد چرب، کالری و کاهش وزن را به فارسی روان و صحیح بنویسید.
۳. حتماً از جداول مارک‌داون (Markdown Tables)، تیترهای واضح و لیست‌های بولتی جهت خوانایی بالا استفاده کنید.

اطلاعات پرونده سلامت بیمار:
- سن: ${profile.age || 'نامشخص'} سال
- جنسیت: ${profile.gender === 'female' ? 'خانم' : 'آقا'}
- قد: ${profile.height || 'نامشخص'} سانتی‌متر
- وزن فعلی: ${profile.weight || 'نامشخص'} کیلوگرم
- سطح فعالیت روزانه: ${getActivityLevelLabel(profile.activityLevel || 'moderate', t)}
- بیماری‌ها و سوابق پزشکی: ${profile.medicalConditions || 'هیچ بیماری خاصی ثبت نشده است'}
- داروهای مصرفی فعلی: ${profile.medications || 'بدون داروی مصرفی'}
- حساسیت‌ها و آلرژی‌ها: ${profile.allergies || 'بدون حساسیت'}
- اهداف سلامت بیمار: ${profile.dietaryGoals || 'بهبود عمومی سلامت'}

وضعیت تغذیه امروز بیمار:
- کالری دریافتی امروز: ${toPersianDigits(dailyCalories)} کیلوکالری
- میزان آب مصرفی امروز: ${toPersianDigits(dailyWater)} میلی‌لیتر

ساختار استاندارد پاسخ:
### 🩺 ۱. پاسخ مستقیم و تشخیص اولیه
(پاسخ صریح و علمی به پرسش دقیق بیمار)

### 📊 ۲. جدول تحلیل اختصاصی پرونده بیمار
(تحلیل علائم و سوال بیمار با توجه به بیماری‌های پرونده مانند کبد چرب، سن و وزن در قالب جدول مارک‌داون منظم شامل: [عامل/شاخص | وضعیت شما | تحلیل بالینی و توصیه])

### 🌿 ۳. مکمل‌ها و دمنوش‌های تخصصی پیشنهادی
(معرفی دمنوش‌های علمی مفید مانند خارمریم، کاسنی، چای سبز یا مکمل‌های مناسب با میزان و نحوه دقیق مصرف)

### ⚠️ ۴. احتیاط‌ها، پرهیزهای غذایی و تداخلات دارویی
(پرهیزهای غذایی لازم برای کبد چرب/شرایط بیمار و تداخلات احتمالی با داروها)

### 📝 ۵. برنامه اقدام عملی روزانه
(توصیه‌های کاربردی برای تغذیه، تحرک و سبک زندگی امروز)

سؤال بیمار:
"${questionToAsk}"`;

    try {
      const fullRes = await sendWorkerAiFullRequest('doctor', systemPrompt);
      setCurrentAnswer(fullRes.reply);

      const modelInfo = {
        model: fullRes.model || 'gpt-4.1-mini-2025-04-14',
        provider: fullRes.provider || 'github-models',
        responseTimeMs: fullRes.debug?.responseTime
      };
      setCurrentModelInfo(modelInfo);

      const postRes = await apiPost<any>('/api/doctor/consultations', {
        question: questionToAsk,
        answer: fullRes.reply,
        model_name: modelInfo.model,
        response_time_ms: modelInfo.responseTimeMs
      });

      if (!postRes.success || !postRes.data) {
        throw new Error(postRes.error || 'Failed to save consultation');
      }

      const returnedItem = postRes.data;
      const newConsultation: DoctorConsultation = {
        id: returnedItem.id,
        question: returnedItem.question,
        answer: returnedItem.answer,
        createdAt: returnedItem.created_at || returnedItem.createdAt || new Date().toISOString(),
        userProfileSnapshot: { ...profile },
        dailyContextSnapshot: {
          caloriesLoggedToday: dailyCalories,
          waterMlToday: dailyWater,
          weightToday: typeof profile.weight === 'number' ? profile.weight : parseFloat(String(profile.weight)) || 0
        },
        aiModel: returnedItem.model_name || modelInfo.model,
        aiProvider: modelInfo.provider,
        responseTimeMs: returnedItem.response_time_ms || modelInfo.responseTimeMs
      };

      setConsultations(prev => [newConsultation, ...prev.filter(c => c.id !== newConsultation.id)]);
      if (!customPrompt) setUserQuery('');
    } catch (err: any) {
      alert(t('doctorAiError', 'خطا در برقراری ارتباط با پزشک هوش مصنوعی: ') + (err?.message || err));
    } finally {
      setIsAskingAi(false);
    }
  };

  // Copy answer to clipboard
  const handleCopyAnswer = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedAnswer(true);
    setTimeout(() => setCopiedAnswer(false), 2500);
  };

  // Delete consultation from local server REST API
  const handleDeleteConsultation = async (id: string) => {
    if (window.confirm(t('doctorDeleteConfirm', 'آیا از حذف این مشاوره پزشکی اطمینان دارید؟'))) {
      try {
        const deleteRes = await apiDelete(`/api/doctor/consultations/${id}`);
        if (!deleteRes.success) {
          throw new Error(deleteRes.error || 'Failed to delete consultation');
        }
        setConsultations(prev => prev.filter(c => c.id !== id));
      } catch (err: any) {
        alert(t('doctorDeleteError', 'خطا در حذف مشاوره: ') + (err?.message || err));
      }
    }
  };

  return (
    <div className="space-y-8 max-w-5xl mx-auto pb-16 animate-fadeIn">
      {/* HEADER BANNER */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-emerald-900 via-teal-900 to-slate-900 text-white p-6 sm:p-8 shadow-xl border border-teal-800/50">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-teal-400 via-emerald-300 to-cyan-400" />
        <div className="absolute -left-10 -bottom-10 w-48 h-48 bg-teal-500/10 rounded-full blur-3xl pointer-events-none" />

        <div className="relative z-10 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2 bg-teal-500/20 text-teal-300 px-3.5 py-1 rounded-full text-xs font-extrabold border border-teal-500/30">
              <Stethoscope className="w-4 h-4 text-emerald-300" />
              <span>{t('doctorTitle', 'پزشک اختصاصی و مشاوره هوشمند سلامتی (AI Doctor)')}</span>
            </div>

            <div className="text-xs text-teal-200/80 font-mono">
              {t('synced', 'ذخیره‌سازی زنده ابری در پایگاه داده Cloudflare D1')}
            </div>
          </div>

          <div className="space-y-1">
            <h1 className="text-2xl sm:text-3xl font-extrabold font-display text-white flex items-center gap-2.5">
              <HeartPulse className="w-8 h-8 text-emerald-400" />
              <span>{t('doctorHeaderTitle', 'دستیار و پزشک اختصاصی هوش مصنوعی (AI Doctor)')}</span>
            </h1>
            <p className="text-xs sm:text-sm text-teal-100 max-w-2xl leading-relaxed">
              {t('doctorHeaderDesc', 'پاسخگویی به پرسش‌های پزشکی، تحلیل علائم بیماری، توصیه دارویی و بررسی آزمایشگاه')}
            </p>
          </div>
        </div>
      </div>

      {/* SECTION 1: USER MEDICAL PROFILE (پرونده سلامت) */}
      <div className="bg-white dark:bg-gray-800 rounded-3xl p-6 shadow-sm border border-gray-100 dark:border-gray-700/80 space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 dark:border-gray-700 pb-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-teal-50 dark:bg-teal-950/60 border border-teal-200 dark:border-teal-800 flex items-center justify-center text-teal-600 shrink-0">
              <User className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-lg font-extrabold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <span>{t('doctorProfileTitle', 'پرونده سلامت و اطلاعات پزشکی شما')}</span>
                <span className="text-[11px] font-bold px-2.5 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300">
                  {t('doctorProfileBadge', 'مبنای تحلیل AI')}
                </span>
              </h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t('doctorProfileDesc', 'این اطلاعات برای ارائه دقیق‌ترین پاسخ‌های تخصصی توسط هوش مصنوعی استفاده می‌شوند.')}
              </p>
            </div>
          </div>

          <button
            onClick={openEditProfile}
            className="px-4 py-2 rounded-2xl bg-teal-50 dark:bg-teal-950/50 hover:bg-teal-100 dark:hover:bg-teal-900 text-teal-700 dark:text-teal-300 text-xs font-bold transition-all border border-teal-200 dark:border-teal-800 flex items-center gap-1.5 cursor-pointer"
          >
            <Pencil className="w-3.5 h-3.5" />
            <span>{t('doctorProfileEditBtn', 'ویرایش پرونده سلامت')}</span>
          </button>
        </div>

        {/* Profile Details Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
          <div className="p-3.5 rounded-2xl bg-gray-50 dark:bg-gray-900/60 border border-gray-100 dark:border-gray-700/80 space-y-1">
            <span className="text-gray-400 text-[11px] block">{t('doctorProfileAgeGender', 'سن و جنسیت:')}</span>
            <span className="font-extrabold text-gray-800 dark:text-gray-200 block text-sm">
              {t('doctorAgeVal', '{{age}} سال ({{gender}})', { age: toPersianDigits(profile.age), gender: profile.gender === 'female' ? t('doctorGenderF', 'خانم') : t('doctorGenderM', 'آقا') })}
            </span>
          </div>

          <div className="p-3.5 rounded-2xl bg-gray-50 dark:bg-gray-900/60 border border-gray-100 dark:border-gray-700/80 space-y-1">
            <span className="text-gray-400 text-[11px] block">{t('doctorProfileHeightWeight', 'قد و وزن:')}</span>
            <span className="font-extrabold text-gray-800 dark:text-gray-200 block text-sm">
              {t('doctorWeightHeightVal', '{{weight}} کیلوگرم / {{height}} سانتی‌متر', { weight: toPersianDigits(profile.weight), height: toPersianDigits(profile.height) })}
            </span>
          </div>

          <div className="p-3.5 rounded-2xl bg-gray-50 dark:bg-gray-900/60 border border-gray-100 dark:border-gray-700/80 space-y-1 col-span-2">
            <span className="text-gray-400 text-[11px] block">{t('doctorProfileActivity', 'سطح فعالیت روزانه:')}</span>
            <span className="font-bold text-teal-700 dark:text-teal-300 block">
              {getActivityLevelLabel(profile.activityLevel || 'moderate', t)}
            </span>
          </div>

          <div className="p-3.5 rounded-2xl bg-amber-50/50 dark:bg-amber-950/20 border border-amber-200/50 dark:border-amber-800/40 space-y-1 col-span-2">
            <span className="text-amber-700 dark:text-amber-400 text-[11px] font-bold flex items-center gap-1">
              <ShieldAlert className="w-3.5 h-3.5" />
              {t('doctorProfileMedCond', 'بیماری‌ها و سوابق پزشکی:')}
            </span>
            <span className="font-semibold text-gray-800 dark:text-gray-200 block">
              {getLocalizedMedicalText(profile.medicalConditions, 'cond', t)}
            </span>
          </div>

          <div className="p-3.5 rounded-2xl bg-indigo-50/50 dark:bg-indigo-950/20 border border-indigo-200/50 dark:border-indigo-800/40 space-y-1 col-span-2">
            <span className="text-indigo-700 dark:text-indigo-400 text-[11px] font-bold flex items-center gap-1">
              <Pill className="w-3.5 h-3.5" />
              {t('doctorProfileMeds', 'داروهای مصرفی و مکمل‌ها:')}
            </span>
            <span className="font-semibold text-gray-800 dark:text-gray-200 block">
              {getLocalizedMedicalText(profile.medications, 'med', t)}
            </span>
          </div>
        </div>

        {/* Live Context Row */}
        <div className="p-4 rounded-2xl bg-gradient-to-r from-teal-50 to-emerald-50 dark:from-teal-950/40 dark:to-emerald-950/40 border border-teal-200 dark:border-teal-800/60 flex flex-wrap items-center justify-between gap-3 text-xs">
          <div className="flex items-center gap-2">
            <Activity className="w-4 h-4 text-teal-600 dark:text-teal-400" />
            <span className="font-bold text-gray-700 dark:text-gray-300">
              {t('doctorLiveContextTitle', 'داده‌های ثبت‌شده امروز شما (خودکار تزریق می‌شود به AI):')}
            </span>
          </div>

          <div className="flex items-center gap-4 font-extrabold">
            <span className="flex items-center gap-1 text-amber-700 dark:text-amber-300">
              <Flame className="w-3.5 h-3.5 text-amber-500" />
              {t('doctorLiveCalories', '{{calories}} کالری امروز', { calories: toPersianDigits(dailyCalories), cal: toPersianDigits(dailyCalories) })}
            </span>
            <span className="flex items-center gap-1 text-teal-700 dark:text-teal-300">
              <Droplets className="w-3.5 h-3.5 text-teal-500" />
              {t('doctorLiveWater', '{{water}} میلی‌لیتر آب', { water: toPersianDigits(dailyWater) })}
            </span>
          </div>
        </div>
      </div>

      {/* SECTION 2: ASK AI DOCTOR */}
      <div className="bg-white dark:bg-gray-800 rounded-3xl p-6 shadow-sm border border-gray-100 dark:border-gray-700/80 space-y-5">
        <div className="space-y-1">
          <h3 className="text-base font-extrabold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-teal-600" />
            <span>{t('doctorNewQueryTitle', 'سوال پزشکی یا مشاوره جدید')}</span>
          </h3>
          <p className="text-xs text-gray-500">
            {t('doctorNewQueryDesc', 'سوال خود درباره تغذیه، داروها، علائم بیماری یا برنامه غذایی مناسب را بنویسید:')}
          </p>
        </div>

        {/* Quick Suggestion Chips */}
        <div className="space-y-2">
          <span className="text-[11px] font-bold text-gray-400 block">{t('doctorSuggestedQueries', 'سوالات پیشنهادی متداول:')}</span>
          <div className="flex flex-wrap gap-2 text-xs">
            {[
              t('doctorQ1', 'آیا وعده غذایی امروز من با بیماری‌ها و داروهایم تداخل دارد؟'),
              t('doctorQ2', 'بهترین میان‌وعده سالم بر اساس سن و وضعیت سلامت من چیست؟'),
              t('doctorQ3', 'با توجه به کبد چرب و علائم من، چه مکمل یا دمنوشی پیشنهاد می‌کنید؟'),
              t('doctorQ4', 'چه نوع فعالیت ورزشی با شرایط فیزیکی من ایمن و مناسب است؟')
            ].map((qText, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => setUserQuery(qText)}
                className="px-3 py-1.5 rounded-xl bg-gray-50 dark:bg-gray-900 hover:bg-teal-50 dark:hover:bg-teal-950/50 text-gray-700 dark:text-gray-300 hover:text-teal-700 dark:hover:text-teal-300 border border-gray-200 dark:border-gray-700 transition-all text-right cursor-pointer"
              >
                {qText}
              </button>
            ))}
          </div>
        </div>

        {/* Query Textarea & Button */}
        <div className="space-y-3">
          <textarea
            value={userQuery}
            onChange={(e) => setUserQuery(e.target.value)}
            rows={4}
            placeholder={t('doctorQueryPlaceholder', 'مثلا: من فشار خون و کبد چرب دارم و امروز ۲۰۰ گرم کباب با ماست یونانی خوردم. آیا مصرف قهوه در عصر برای من مناسب است؟')}
            className="w-full p-4 rounded-2xl text-xs sm:text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-teal-500 transition-all leading-relaxed"
          />

          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleAskDoctor()}
                disabled={!userQuery.trim() || isAskingAi}
                className="px-6 py-3 rounded-2xl bg-teal-600 hover:bg-teal-500 disabled:opacity-50 text-white font-extrabold text-xs sm:text-sm shadow-md transition-all flex items-center gap-2 cursor-pointer"
              >
                {isAskingAi ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span>{t('doctorAiAnalyzing', 'پزشک در حال بررسی پرونده و تحلیل...')}</span>
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    <span>{t('doctorAiSubmit', 'دریافت مشاوره تخصصی پزشکی')}</span>
                  </>
                )}
              </button>

              <VoiceInputButton
                currentValue={userQuery}
                onUpdate={(val) => setUserQuery(val)}
                disabled={isAskingAi}
                title={t('voiceInputNormal', 'ورودی صوتی')}
              />
            </div>

            {userQuery && (
              <button
                onClick={() => setUserQuery('')}
                className="text-xs text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                {t('doctorClearText', 'پاکسازی متن')}
              </button>
            )}
          </div>
        </div>

        {/* AI Answer Display */}
        {currentAnswer && (
          <div className="p-6 rounded-3xl bg-teal-50/40 dark:bg-teal-950/30 border border-teal-200 dark:border-teal-800/80 space-y-4 animate-fadeIn">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-teal-200/60 dark:border-teal-800/60 pb-3">
              <div className="flex items-center gap-2 text-teal-800 dark:text-teal-200 font-extrabold text-sm">
                <Stethoscope className="w-5 h-5 text-teal-600" />
                <span>{t('doctorAiAnswerTitle', 'پاسخ تخصصی پزشک هوش مصنوعی')}</span>
              </div>

              <div className="flex items-center gap-2">
                {currentModelInfo && (
                  <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-xl bg-teal-100/70 dark:bg-teal-900/50 text-[11px] font-mono text-teal-800 dark:text-teal-200 border border-teal-300/50 dark:border-teal-700/50">
                    <Cpu className="w-3.5 h-3.5 text-teal-600 dark:text-teal-400" />
                    <span>{currentModelInfo.model || 'gpt-4.1-mini-2025-04-14'}</span>
                    {currentModelInfo.responseTimeMs && (
                      <span className="text-[10px] text-teal-600 dark:text-teal-400">
                        ({(currentModelInfo.responseTimeMs / 1000).toFixed(1)}s)
                      </span>
                    )}
                  </div>
                )}

                <button
                  onClick={() => handleCopyAnswer(currentAnswer)}
                  className="px-3 py-1.5 rounded-xl bg-white dark:bg-gray-800 hover:bg-teal-100 dark:hover:bg-teal-900 text-teal-700 dark:text-teal-300 text-xs font-bold border border-teal-200 dark:border-teal-700 transition-all flex items-center gap-1.5 cursor-pointer shadow-xs"
                >
                  {copiedAnswer ? (
                    <>
                      <Check className="w-3.5 h-3.5 text-emerald-600" />
                      <span>{t('doctorCopied', 'کپی شد!')}</span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-3.5 h-3.5" />
                      <span>{t('doctorCopyAnswer', 'کپی پاسخ')}</span>
                    </>
                  )}
                </button>
              </div>
            </div>

            <div className="text-xs sm:text-sm text-gray-800 dark:text-gray-200 leading-loose font-sans">
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={doctorMarkdownComponents}>
                {currentAnswer}
              </ReactMarkdown>
            </div>
          </div>
        )}
      </div>

      {/* SECTION 3: CONSULTATIONS HISTORY (تاریخچه مشاوره‌ها) */}
      <div className="bg-white dark:bg-gray-800 rounded-3xl p-6 shadow-sm border border-gray-100 dark:border-gray-700/80 space-y-5">
        <div className="flex items-center justify-between border-b border-gray-100 dark:border-gray-700 pb-3">
          <h3 className="text-base font-extrabold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-teal-600" />
            <span>{t('doctorHistoryTitle', 'تاریخچه مشاوره‌های پزشکی ثبت‌شده')}</span>
            <span className="text-xs font-bold px-2.5 py-0.5 rounded-full bg-teal-100 dark:bg-teal-950 text-teal-700 dark:text-teal-300">
              {t('doctorHistoryCount', '{{count}} مورد', { count: toPersianDigits(consultations.length) })}
            </span>
          </h3>
        </div>

        {isLoadingConsultations ? (
          <div className="text-center py-8 text-xs text-gray-400 space-y-2">
            <RefreshCw className="w-6 h-6 animate-spin mx-auto text-teal-600" />
            <p>{t('doctorHistoryLoading', 'در حال بارگذاری مشاوره‌های ابری از پایگاه داده Cloudflare D1...')}</p>
          </div>
        ) : consultationError ? (
          <div className="text-center py-8 px-4 rounded-2xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800 text-rose-700 dark:text-rose-300 space-y-3">
            <AlertCircle className="w-8 h-8 mx-auto text-rose-500" />
            <p className="text-xs font-bold">{consultationError}</p>
            <button
              onClick={fetchConsultations}
              className="px-4 py-2 text-xs font-bold bg-rose-600 hover:bg-rose-700 text-white rounded-xl transition-all cursor-pointer inline-flex items-center gap-2"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              <span>{t('retry', 'تلاش مجدد')}</span>
            </button>
          </div>
        ) : consultations.length === 0 ? (
          <div className="text-center py-10 text-gray-400 space-y-2">
            <FileText className="w-10 h-10 mx-auto text-gray-300 dark:text-gray-600" />
            <p className="text-xs font-bold">{t('doctorHistoryEmpty', 'هنوز هیچ مشاوره پزشکی ثبت نشده است.')}</p>
            <p className="text-[11px]">{t('doctorHistoryEmptyDesc', 'اولین سوال خود را در کادر بالا بپرسید تا در پرونده ابری شما ذخیره شود.')}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {consultations.map((item) => (
              <div
                key={item.id}
                className="p-5 rounded-2xl bg-gray-50 dark:bg-gray-900/60 border border-gray-100 dark:border-gray-700/80 space-y-3 relative group"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-[10px] font-bold text-teal-600 dark:text-teal-400 bg-teal-50 dark:bg-teal-950 px-2 py-0.5 rounded-md inline-block">
                        📅 {formatGregorianDate(item.createdAt, lang, 'medium')}
                      </span>
                      {item.aiModel && (
                        <span className="text-[10px] font-mono text-slate-600 dark:text-slate-400 bg-slate-200/60 dark:bg-slate-800 px-2 py-0.5 rounded-md flex items-center gap-1 border border-slate-300/40 dark:border-slate-700">
                          <Cpu className="w-3 h-3 text-teal-500" />
                          <span>{item.aiModel}</span>
                        </span>
                      )}
                    </div>
                    <h4 className="text-xs sm:text-sm font-extrabold text-gray-900 dark:text-gray-100">
                      {t('doctorHistoryQuestion', 'سوال: {{q}}', { q: item.question })}
                    </h4>
                  </div>

                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleCopyAnswer(item.answer)}
                      className="p-1.5 text-gray-400 hover:text-teal-600 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-all cursor-pointer"
                      title={t('doctorCopyAnswer', 'کپی پاسخ')}
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => handleDeleteConsultation(item.id)}
                      className="p-1.5 text-gray-400 hover:text-red-500 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-all cursor-pointer"
                      title={t('doctorDeleteCons', 'حذف مشاوره')}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                <div className="p-4 rounded-xl bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 text-xs text-gray-700 dark:text-gray-300 leading-relaxed max-h-80 overflow-y-auto custom-scrollbar">
                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={doctorMarkdownComponents}>
                    {item.answer}
                  </ReactMarkdown>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* EDIT MEDICAL PROFILE MODAL */}
      {isEditingProfile && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 animate-fadeIn">
          <div className="bg-white dark:bg-gray-800 rounded-3xl max-w-lg w-full p-6 shadow-2xl border border-gray-100 dark:border-gray-700 space-y-4 relative max-h-[90vh] overflow-y-auto custom-scrollbar">
            <button
              onClick={() => setIsEditingProfile(false)}
              className="absolute top-4 left-4 p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-all cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="space-y-1">
              <span className="text-xs font-bold text-teal-600 flex items-center gap-1">
                <User className="w-4 h-4" />
                {t('doctorEditProfileHeader', 'ویرایش پرونده سلامت و فیزیک')}
              </span>
              <h3 className="text-xl font-extrabold text-gray-900 dark:text-gray-100">
                {t('doctorEditProfileTitle', 'بروزرسانی شاخص‌های فیزیکی و پزشکی')}
              </h3>
            </div>

            <form onSubmit={handleSaveProfile} className="space-y-3 text-xs">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="font-bold text-gray-700 dark:text-gray-300">{t('doctorEditAge', 'سن (سال):')}</label>
                  <input
                    type="text"
                    value={profileForm.age || ''}
                    onChange={(e) => setProfileForm({ ...profileForm, age: e.target.value })}
                    className="w-full px-3 py-2 rounded-xl bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700"
                    required
                  />
                </div>

                <div className="space-y-1">
                  <label className="font-bold text-gray-700 dark:text-gray-300">{t('doctorEditGender', 'جنسیت:')}</label>
                  <select
                    value={profileForm.gender || 'male'}
                    onChange={(e) => setProfileForm({ ...profileForm, gender: e.target.value as any })}
                    className="w-full px-3 py-2 rounded-xl bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700"
                  >
                    <option value="male">{t('doctorGenderM', 'آقا')}</option>
                    <option value="female">{t('doctorGenderF', 'خانم')}</option>
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="font-bold text-gray-700 dark:text-gray-300">{t('doctorEditHeight', 'قد (سانتی‌متر):')}</label>
                  <input
                    type="text"
                    value={profileForm.height || ''}
                    onChange={(e) => setProfileForm({ ...profileForm, height: e.target.value })}
                    className="w-full px-3 py-2 rounded-xl bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700"
                  />
                </div>

                <div className="space-y-1">
                  <label className="font-bold text-gray-700 dark:text-gray-300">{t('doctorEditWeight', 'وزن فعلی (کیلوگرم):')}</label>
                  <input
                    type="text"
                    value={profileForm.weight || ''}
                    onChange={(e) => setProfileForm({ ...profileForm, weight: e.target.value })}
                    className="w-full px-3 py-2 rounded-xl bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="font-bold text-gray-700 dark:text-gray-300">{t('doctorEditActivity', 'سطح فعالیت روزانه:')}</label>
                <select
                  value={profileForm.activityLevel || 'moderate'}
                  onChange={(e) => setProfileForm({ ...profileForm, activityLevel: e.target.value as any })}
                  className="w-full px-3 py-2 rounded-xl bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700"
                >
                  {['sedentary', 'light', 'moderate', 'high', 'athlete'].map((key) => (
                    <option key={key} value={key}>{getActivityLevelLabel(key, t)}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-1">
                <label className="font-bold text-gray-700 dark:text-gray-300">{t('doctorEditMedCond', 'بیماری‌های خاص و سوابق پزشکی:')}</label>
                <textarea
                  value={profileForm.medicalConditions || ''}
                  onChange={(e) => setProfileForm({ ...profileForm, medicalConditions: e.target.value })}
                  placeholder={t('doctorEditMedCondPh', 'مثلا: کبد چرب، فشار خون، دیابت، دیسک کمر...')}
                  rows={2}
                  className="w-full px-3 py-2 rounded-xl bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700"
                />
              </div>

              <div className="space-y-1">
                <label className="font-bold text-gray-700 dark:text-gray-300">{t('doctorEditMeds', 'داروهای مصرفی و مکمل‌ها:')}</label>
                <input
                  type="text"
                  value={profileForm.medications || ''}
                  onChange={(e) => setProfileForm({ ...profileForm, medications: e.target.value })}
                  placeholder={t('doctorEditMedsPh', 'مثلا: متفورمین، لووتیروکسین، لوزارتان')}
                  className="w-full px-3 py-2 rounded-xl bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700"
                />
              </div>

              <div className="space-y-1">
                <label className="font-bold text-gray-700 dark:text-gray-300">{t('doctorEditAllergies', 'حساسیت‌های دارویی و غذایی:')}</label>
                <input
                  type="text"
                  value={profileForm.allergies || ''}
                  onChange={(e) => setProfileForm({ ...profileForm, allergies: e.target.value })}
                  placeholder={t('doctorEditAllergiesPh', 'مثلا: حسایست به پنی‌سیلین یا بادمجان')}
                  className="w-full px-3 py-2 rounded-xl bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700"
                />
              </div>

              <div className="space-y-1">
                <label className="font-bold text-gray-700 dark:text-gray-300">{t('doctorEditGoals', 'اهداف سلامتی و تغذیه‌ای:')}</label>
                <input
                  type="text"
                  value={profileForm.dietaryGoals || ''}
                  onChange={(e) => setProfileForm({ ...profileForm, dietaryGoals: e.target.value })}
                  placeholder={t('doctorEditGoalsPh', 'مثلا: کاهش چربی احشایی شکم و تنظیم قند')}
                  className="w-full px-3 py-2 rounded-xl bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700"
                />
              </div>

              <div className="flex items-center gap-3 pt-3">
                <button
                  type="submit"
                  disabled={isSavingProfile}
                  className="flex-1 py-2.5 px-4 rounded-2xl bg-teal-600 hover:bg-teal-500 text-white font-extrabold text-xs shadow-md transition-all flex items-center justify-center gap-1.5 cursor-pointer"
                >
                  <Save className="w-4 h-4" />
                  <span>{isSavingProfile ? t('doctorSaving', 'در حال ذخیره‌سازی...') : t('doctorSaveToDb', 'ذخیره در پایگاه داده Cloudflare D1')}</span>
                </button>
                <button
                  type="button"
                  onClick={() => setIsEditingProfile(false)}
                  className="py-2.5 px-4 rounded-2xl bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-bold cursor-pointer"
                >
                  {t('doctorCancel', 'انصراف')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
